import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Link2, Loader2, Unlink, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { salesQuoteHref } from './LinkedQuoteAction';
import { HelpLink } from './HelpLink';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ref = (q) => `#${q.quote_number || q.id.slice(0, 8).toUpperCase()}`;
const money = (v) => (v != null ? ` · $${Number(v).toFixed(2)}` : '');

const QuotePicker = ({ open, onOpenChange, token, onPick, busy }) => {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      axios.get(`${API_URL}/admin/contact-messages/linkable-quotes`, { headers: { Authorization: `Bearer ${token}` }, params: { search: search || undefined } })
        .then((r) => setItems(r.data)).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, search, token]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="link-quote-dialog">
        <DialogHeader>
          <DialogTitle>Link Sales Quote</DialogTitle>
          <DialogDescription>Pick the existing Sales Quote prepared for this request. Quotes that already belong to another request are hidden.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by customer, email or quote #" className="pl-9" data-testid="link-quote-search" />
        </div>
        <div className="max-h-80 overflow-y-auto divide-y border rounded-lg" data-testid="link-quote-list">
          {items === null ? <p className="p-4 text-sm text-neutral-500">Loading…</p> : !items.length ? <p className="p-4 text-sm text-neutral-500" data-testid="link-quote-empty">No available Sales Quotes found.</p> : items.map((q) => (
            <button key={q.id} type="button" disabled={busy} onClick={() => onPick(q)} className="w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors disabled:opacity-50" data-testid={`link-quote-option-${q.id}`}>
              <p className="text-sm font-medium"><span className="font-mono">{ref(q)}</span> · {q.customer_name || 'No name'}</p>
              <p className="text-xs text-neutral-500"><span className="capitalize">{(q.status || 'pending').replace(/_/g, ' ')}</span>{money(q.total)} · {q.customer_email} · {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Manual, admin-only link between a CMS quote-form Contact message and one existing Sales Quote.
export const ContactQuoteLink = ({ contact, token, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const lq = contact.linked_quote;
  const link = async (q) => {
    if (!window.confirm(`Link Sales Quote ${ref(q)} (${q.customer_name || q.customer_email}) to this request?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/admin/contact-messages/${contact.id}/link-quote`, { quote_id: q.id }, auth);
      toast.success('Sales Quote linked');
      setOpen(false);
      onChange({ linked_quote_id: q.id, linked_quote: r.data.linked_quote });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not link the Sales Quote'); } finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm(`Unlink Sales Quote ${ref(lq)} from this request? Conversion tracking for this request will stop until another quote is linked.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/admin/contact-messages/${contact.id}/link-quote`, auth);
      toast.success('Sales Quote unlinked');
      onChange({ linked_quote_id: undefined, linked_quote: undefined });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not unlink'); } finally { setBusy(false); }
  };
  if (lq) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 border border-green-200 bg-green-50 rounded-lg" data-testid={`contact-linked-quote-${contact.id}`}>
        <div className="text-sm">
          <p className="font-medium text-neutral-900">Linked Sales Quote <span className="font-mono" data-testid="contact-linked-quote-number">{ref(lq)}</span></p>
          <p className="text-neutral-600 capitalize" data-testid="contact-linked-quote-status">Status: {(lq.status || 'pending').replace(/_/g, ' ')}{money(lq.total)}</p>
        </div>
        <div className="flex gap-2">
          {lq.status !== 'deleted' && (
            <Button asChild size="sm" variant="outline" data-testid="contact-view-sales-quote-btn">
              <a href={salesQuoteHref(lq.id)} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" />View Sales Quote</a>
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={unlink} disabled={busy} className="text-red-600 hover:text-red-700" data-testid="contact-unlink-quote-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Unlink className="w-4 h-4 mr-1.5" />}Unlink Sales Quote
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 p-3 border border-neutral-200 rounded-lg" data-testid={`contact-link-panel-${contact.id}`}>
      <p className="text-sm text-neutral-600">No Sales Quote linked. Linking one makes this request conversion-trackable. <HelpLink topic="quote-linking" /></p>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="contact-link-quote-btn"><Link2 className="w-4 h-4 mr-1.5" />Link Sales Quote</Button>
      <QuotePicker open={open} onOpenChange={setOpen} token={token} onPick={link} busy={busy} />
    </div>
  );
};
